import { useEffect, useRef, useState } from 'react';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import type { EntityRiskValues, EntityTab, RiskDataMap } from '../../types';
import {
  mapProposalDetailsToRiskDataMapForEntities,
  mapProposalDetailsToRiskEntities,
  riskEntitiesFingerprint,
} from '../utils/map-api.util';

export const DEFAULT_RISK_VALUES: EntityRiskValues = {
  perfilDoGrupo: 'UNIFORME',
  sdQuantidadeDeVidas: '',
  sdCapitalIndividual: '',
  colQuantidadeDeVidas: '',
  colCapitalIndividual: '',
  tipoDePerfil: 'COLABORADORES',
  uniQuantidadeDeVidas: '',
  uniCapitalIndividual: '',
};

const RISK_FIELD_NAMES: Array<keyof EntityRiskValues> = [
  'perfilDoGrupo',
  'sdQuantidadeDeVidas',
  'sdCapitalIndividual',
  'colQuantidadeDeVidas',
  'colCapitalIndividual',
  'tipoDePerfil',
  'uniQuantidadeDeVidas',
  'uniCapitalIndividual',
];

type RiskEntityFormApi = {
  getFieldValue: (name: string) => unknown;
  setFieldValue: (name: string, value: unknown) => void;
};

type TabChangeOptions = {
  preserveSubmitAttempted?: boolean;
};

type Params = {
  form: any;
  setSubmitAttempted: (value: boolean) => void;
};

type Result = {
  entities: EntityTab[];
  hasMultipleEntities: boolean;
  activeEntityId: string;
  handleTabChange: (newEntityId: string, options?: TabChangeOptions) => void;

  captureAllEntities: () => RiskDataMap;
};

export function useStepDadosDoRiscoEntityTabs({
  form: formRaw,
  setSubmitAttempted,
}: Params): Result {
  const form = formRaw as RiskEntityFormApi;
  const { policyId } = useQuoteParams();
  const { data: proposalDetails } = useProposalDetails(policyId || undefined);

  const entities = mapProposalDetailsToRiskEntities(proposalDetails);
  const hasMultipleEntities = entities.length > 1;
  const entitiesFingerprint = riskEntitiesFingerprint(proposalDetails);

  const [activeEntityId, setActiveEntityId] = useState<string>('');
  const riskDataMapRef = useRef<RiskDataMap>({});
  const syncedEntitiesFingerprintRef = useRef('');

  function captureCurrentValues(): EntityRiskValues {
    return RISK_FIELD_NAMES.reduce<EntityRiskValues>(
      (acc, key) => {
        (acc as Record<string, unknown>)[key] = form.getFieldValue(key);
        return acc;
      },
      { ...DEFAULT_RISK_VALUES }
    );
  }

  function restoreValues(values: EntityRiskValues) {
    RISK_FIELD_NAMES.forEach(key => {
      form.setFieldValue(key, values[key]);
    });
  }

  useEffect(() => {
    if (!proposalDetails) return;

    const fingerprintChanged =
      entitiesFingerprint !== syncedEntitiesFingerprintRef.current;

    if (fingerprintChanged) {
      riskDataMapRef.current = mapProposalDetailsToRiskDataMapForEntities(
        proposalDetails,
        entities
      );
      syncedEntitiesFingerprintRef.current = entitiesFingerprint;
    }

    if (entities.length === 0) {
      if (activeEntityId) {
        setActiveEntityId('');
      }
      return;
    }

    const entityIdSet = new Set(entities.map(entity => entity.id));
    const needsActiveReset = !entityIdSet.has(activeEntityId);
    const nextActiveId = needsActiveReset ? entities[0].id : activeEntityId;

    if (needsActiveReset) {
      setActiveEntityId(nextActiveId);
    }

    if (fingerprintChanged || needsActiveReset) {
      const saved = riskDataMapRef.current[nextActiveId];
      restoreValues(saved ?? DEFAULT_RISK_VALUES);
    }
  }, [entities, entitiesFingerprint, activeEntityId, proposalDetails]);

  function handleTabChange(
    newEntityId: string,
    options?: TabChangeOptions
  ) {
    if (activeEntityId) {
      riskDataMapRef.current[activeEntityId] = captureCurrentValues();
    }
    const nextValues =
      riskDataMapRef.current[newEntityId] ?? DEFAULT_RISK_VALUES;
    restoreValues(nextValues);
    if (!options?.preserveSubmitAttempted) {
      setSubmitAttempted(false);
    }
    setActiveEntityId(newEntityId);
  }

  function captureAllEntities(): RiskDataMap {
    return {
      ...riskDataMapRef.current,
      [activeEntityId]: captureCurrentValues(),
    };
  }

  return {
    entities,
    hasMultipleEntities,
    activeEntityId,
    handleTabChange,
    captureAllEntities,
  };
}
